import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import ModalBody from '../../Modal/ModalBody';
import Button from '../../Button/Button';
import ProductImage from '../../LegacyOrder/ProductImage';
import SellFromHomeWhatHappensNextDrawer from './SellFromHomeWhatHappensNextDrawer';
import { ReactComponent as InfoIconSecondary } from '../../../assets/images/svg/sfh-info-secondary.svg';
import { ReactComponent as InfoIconBlue } from '../../../assets/images/svg/sfh-info-blue.svg';
import { ReactComponent as HandCoins } from '../../../assets/images/svg/hand-coins.svg';
import { ReactComponent as Close } from '../../../assets/images/svg/close.svg';
import { formatMoneyNumeral } from '../../../util/helpers/numeralHelpers';
import { pxToRem } from '../../../assets/styles/helper';
import themeColors from '../../../assets/styles/themeColors';
import { variables } from '../../../assets/styles/variables';

const SUBTITLE_AND_CAPTION_COLOR = themeColors.colorTextDark;
const PAYOUT_PILL_BG = 'rgba(0, 178, 153, 0.05)';
const VIEW_ORDER_COLOR = '#1C4792';

const ContentWrap = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

const CloseButton = styled.button`
  position: absolute;
  top: ${pxToRem(8)};
  right: ${pxToRem(8)};
  background: transparent;
  border: none;
  padding: ${pxToRem(8)};
  cursor: pointer;
  z-index: 1;

  > svg {
    width: ${pxToRem(20)};
    height: ${pxToRem(20)};
    color: ${themeColors.colorTextPrimary};
  }
`;

const CenterColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${pxToRem(8)};
  padding: ${pxToRem(8)} 0;
`;

// Pushes the whole confirmation block to the bottom of the modal.
const TopCenterColumn = styled(CenterColumn)`
  margin-top: auto;
`;

const ImageWrap = styled.div`
  > img {
    width: ${pxToRem(200)};
    height: ${pxToRem(200)};
    object-fit: contain;
    margin: 0;
  }
`;

const Title = styled.h2`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(22)};
  font-weight: 700;
  color: ${themeColors.colorTextPrimary};
  margin: ${pxToRem(8)} 0 0;
  text-align: center;
`;

const Subtitle = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(14)};
  font-weight: 400;
  color: ${SUBTITLE_AND_CAPTION_COLOR};
  text-align: center;
`;

const ProductCaption = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(24)};
  font-weight: 700;
  color: ${SUBTITLE_AND_CAPTION_COLOR};
  text-align: center;
  margin-top: ${pxToRem(2)};
`;

const PriceGuaranteed = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(14)};
  color: ${themeColors.colorTextSecondary};
  text-align: center;
  margin-bottom: ${pxToRem(12)};
`;

const InfoPill = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${pxToRem(8)};
  width: 100%;
  background: ${themeColors.colorBackgroundSecondary};
  border: 1px solid ${themeColors.colorCardBorder};
  border-radius: ${pxToRem(6)};
  padding: ${pxToRem(8)} ${pxToRem(16)};
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(14)};
  color: ${themeColors.colorTextSecondary};

  > svg {
    width: ${pxToRem(18)};
    height: ${pxToRem(18)};
    flex-shrink: 0;
  }
`;

const PayoutPill = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${pxToRem(8)};
  width: 100%;
  background: ${PAYOUT_PILL_BG};
  border: 1px solid ${themeColors.colorBadgeSuccess};
  border-radius: ${pxToRem(6)};
  padding: ${pxToRem(8)} ${pxToRem(16)};
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(14)};
  color: ${themeColors.colorPayoutSuccess};

  > svg {
    width: ${pxToRem(22)};
    height: ${pxToRem(22)};
    flex-shrink: 0;
  }

  > b {
    font-weight: 700;
  }
`;

const WhatHappensNextLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${pxToRem(6)};
  background: none;
  border: none;
  padding: ${pxToRem(4)} 0;
  margin-top: ${pxToRem(12)};
  cursor: pointer;
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(14)};
  color: ${themeColors.colorPrimary};
  text-decoration: underline;

  > svg {
    width: ${pxToRem(16)};
    height: ${pxToRem(16)};
  }
`;

const Footer = styled.div`
  padding-top: ${pxToRem(12)};

  > button {
    width: 100%;
    color: ${VIEW_ORDER_COLOR};
    border-color: ${VIEW_ORDER_COLOR};
  }
`;

const SellFromHomeConfirmation = ({
  selectedHit,
  quantity,
  lockedUnitPrice,
  lockedPayout,
  handleClose,
  handleViewOrder,
}) => {
  const { t } = useTranslation();
  const [isWhatHappensOpen, setIsWhatHappensOpen] = useState(false);

  const productCaption = `${selectedHit?.WeightCaption ?? ''} ${
    selectedHit?.Caption ?? ''
  }`.trim();

  const onViewOrderClick = () => {
    if (typeof handleViewOrder === 'function') handleViewOrder();
  };

  return (
    <ContentWrap>
      <CloseButton
        type="button"
        onClick={handleClose}
        aria-label={t('common.close')}
        data-cy="button-sfh-confirmation-close"
      >
        <Close />
      </CloseButton>
      <ModalBody>
        <TopCenterColumn>
          <ImageWrap>
            <ProductImage
              productCode={selectedHit?.Code}
              width={200}
              height={200}
            />
          </ImageWrap>
          <Title>{t('sellWizard.sfhWizard.confirmation.title')}</Title>
          <Subtitle>{t('sellWizard.sfhWizard.confirmation.subtitle')}</Subtitle>
          <ProductCaption>
            {t('sellWizard.sfhWizard.review.productLine', {
              count: quantity,
              caption: productCaption,
            })}
          </ProductCaption>
          <PriceGuaranteed>
            {t('sellWizard.sfhWizard.confirmation.priceGuaranteed', {
              price: formatMoneyNumeral(lockedUnitPrice),
            })}
          </PriceGuaranteed>
        </TopCenterColumn>

        <InfoPill>
          <InfoIconSecondary />
          {t('sellWizard.sfhWizard.confirmation.dropOff')}
        </InfoPill>

        <div style={{ height: pxToRem(12) }} />

        <PayoutPill>
          <HandCoins />
          {t('sellWizard.sfhWizard.confirmation.payoutReceivable')}
          <b>{formatMoneyNumeral(lockedPayout)}</b>
        </PayoutPill>

        <CenterColumn>
          <WhatHappensNextLink
            type="button"
            onClick={() => setIsWhatHappensOpen(true)}
            data-cy="link-sfh-what-happens-next"
          >
            <InfoIconBlue />
            {t('sellWizard.sfhWizard.confirmation.whatHappensNext')}
          </WhatHappensNextLink>
        </CenterColumn>

        <Footer>
          <Button
            variant="primaryOutlined"
            textTransform="none"
            onClick={onViewOrderClick}
            data-cy="button-sfh-view-order"
          >
            {t('sellWizard.sfhWizard.confirmation.viewOrder')}
          </Button>
        </Footer>
      </ModalBody>

      <SellFromHomeWhatHappensNextDrawer
        isOpen={isWhatHappensOpen}
        onClose={() => setIsWhatHappensOpen(false)}
      />
    </ContentWrap>
  );
};

SellFromHomeConfirmation.propTypes = {
  selectedHit: PropTypes.shape({
    Code: PropTypes.string,
    Caption: PropTypes.string,
    WeightCaption: PropTypes.string,
  }),
  quantity: PropTypes.number,
  lockedUnitPrice: PropTypes.number,
  lockedPayout: PropTypes.number,
  handleClose: PropTypes.func,
  handleViewOrder: PropTypes.func,
};

export default SellFromHomeConfirmation;
